import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RULE_LABELS } from '@swi/contracts';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import * as api from '../api/endpoints';
import { isApiError } from '../api/problem';
import { PageHeader } from '../components/layout/PageHeader';
import {
  Button,
  Card,
  CardHeader,
  ErrorNote,
  Field,
  Modal,
  Pill,
  Skeleton,
  StateBadge,
  Textarea,
  buttonClass,
} from '../components/ui/primitives';
import { useAuth } from '../lib/auth';
import {
  coords,
  dateTime,
  duration,
  money,
  moneyExact,
  number,
  relative,
} from '../lib/format';

/** One reservation: live flight readouts, price breakdown, boarding pass, actions. */
export function ReservationDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { isStaff, hasRole, user } = useAuth();
  const queryClient = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const reservation = useQuery({
    queryKey: ['reservation', id],
    queryFn: () => api.getReservation(id),
    refetchInterval: (query) => (query.state.data?.state === 'IN_FLIGHT' ? 10_000 : false),
  });

  const telemetry = useQuery({
    queryKey: ['telemetry', id],
    queryFn: () => api.getTelemetry(id),
    enabled: reservation.data?.state === 'IN_FLIGHT' || reservation.data?.state === 'COMPLETED',
    refetchInterval: reservation.data?.state === 'IN_FLIGHT' ? 10_000 : false,
  });

  const notifications = useQuery({
    queryKey: ['notifications', id],
    queryFn: () => api.listNotifications({ reservationId: id }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['reservation', id] });
    void queryClient.invalidateQueries({ queryKey: ['reservations'] });
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const onError = (error: unknown) => {
    if (isApiError(error)) {
      toast.error(error.humanMessage, { description: error.technicalDetail });
    } else {
      toast.error(error instanceof Error ? error.message : 'Operace se nepovedla');
    }
  };

  const confirm = useMutation({
    mutationFn: () => api.confirmReservation(id),
    onSuccess: (updated) => {
      // OP-03 has two outcomes since change C02; saying "potvrzena" for a request
      // that is only waiting for the fleet owner would be a lie to the operator.
      toast.success(
        updated.state === 'PENDING_APPROVAL'
          ? 'Žádost odešla majiteli flotily ke schválení'
          : 'Rezervace potvrzena',
      );
      invalidate();
    },
    onError,
  });

  const approve = useMutation({
    mutationFn: () => api.approveReservation(id),
    onSuccess: () => {
      toast.success('Schváleno a potvrzeno');
      invalidate();
    },
    onError,
  });

  const reject = useMutation({
    mutationFn: () => api.rejectReservation(id, { reason: rejectReason }),
    onSuccess: () => {
      toast.success('Rezervace zamítnuta');
      setRejectOpen(false);
      setRejectReason('');
      invalidate();
    },
    onError,
  });

  const start = useMutation({
    mutationFn: () => api.startReservation(id),
    onSuccess: () => {
      toast.success('Let odstartoval');
      invalidate();
    },
    onError,
  });

  const complete = useMutation({
    mutationFn: () => api.completeReservation(id),
    onSuccess: () => {
      toast.success('Let uzavřen');
      invalidate();
    },
    onError,
  });

  const cancel = useMutation({
    mutationFn: () => api.cancelReservation(id, { reason: cancelReason }),
    onSuccess: (updated) => {
      toast.success('Rezervace zrušena', {
        description: `Vratka ${moneyExact(updated.refundIfCancelledNowCzk)}`,
      });
      setCancelOpen(false);
      setCancelReason('');
      invalidate();
    },
    onError,
  });

  if (reservation.isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }
  if (reservation.isError || !reservation.data) {
    return <ErrorNote title="Rezervaci se nepodařilo načíst" />;
  }

  const data = reservation.data;
  const latest = telemetry.data?.at(-1);
  const canCancel = data.allowedTransitions.includes('CANCELLED');
  // OP-05 is gated on ADMIN alone (REQ-17): the dispatcher raised the request and
  // must not be the one answering it.
  const isApprover = hasRole('ADMIN');
  const canDecide = isApprover && data.state === 'PENDING_APPROVAL';
  const canConfirm = isStaff && data.state === 'DRAFT';
  const canStart = isStaff && data.allowedTransitions.includes('IN_FLIGHT');
  const canComplete = isStaff && data.allowedTransitions.includes('COMPLETED');
  const isOwn = data.renter.id === user?.id;

  return (
    <>
      <PageHeader
        title={`${data.airship.name} · ${data.departureHub.code} → ${data.arrivalHub.code}`}
        description={data.purpose ?? undefined}
        actions={
          <>
            <Link to="/reservations" className={buttonClass('ghost', 'sm')}>
              ← Rezervace
            </Link>
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              Tisk / boarding pass
            </Button>
            {canConfirm && (
              <Button variant="primary" size="sm" loading={confirm.isPending} onClick={() => confirm.mutate()}>
                {data.airship.requiresApproval ? 'Poslat ke schválení' : 'Potvrdit'}
              </Button>
            )}
            {canDecide && (
              <Button variant="primary" size="sm" loading={approve.isPending} onClick={() => approve.mutate()}>
                Schválit
              </Button>
            )}
            {canDecide && (
              <Button variant="danger" size="sm" onClick={() => setRejectOpen(true)}>
                Zamítnout
              </Button>
            )}
            {canStart && (
              <Button variant="primary" size="sm" loading={start.isPending} onClick={() => start.mutate()}>
                Odstartovat
              </Button>
            )}
            {canComplete && (
              <Button variant="primary" size="sm" loading={complete.isPending} onClick={() => complete.mutate()}>
                Uzavřít let
              </Button>
            )}
            {canCancel && (isOwn || isStaff) && (
              <Button variant="danger" size="sm" onClick={() => setCancelOpen(true)}>
                Zrušit
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader
              title={
                <span className="flex flex-wrap items-center gap-2">
                  <span className="mono text-[var(--accent)]">{data.airship.registration}</span>
                  <StateBadge state={data.state} />
                  {data.state === 'CONFIRMED' && (
                    <Pill tone="info">odlet {relative(data.startAt)}</Pill>
                  )}
                  {data.state === 'PENDING_APPROVAL' && data.approval !== null && (
                    <Pill tone="warn">lhůta {relative(data.approval.deadlineAt)}</Pill>
                  )}
                </span>
              }
              subtitle={`Rezervace ${data.id}`}
            />

            <dl className="grid gap-2 sm:grid-cols-2">
              <Row label="Nájemce" value={data.renter.displayName} />
              <Row
                label="Pilot"
                value={
                  data.pilot === null
                    ? 'nepřiřazen'
                    : `${data.pilot.displayName} (licence ${data.pilot.pilotLicenseClass})`
                }
              />
              <Row label="Odlet" value={`${dateTime(data.startAt)} · ${data.departureHub.name}`} />
              <Row label="Přílet" value={`${dateTime(data.endAt)} · ${data.arrivalHub.name}`} />
              <Row label="Délka" value={duration(data.startAt, data.endAt)} />
              <Row label="Pasažérů" value={`${String(data.passengers)} / ${String(data.airship.capacityPax)}`} />
              <Row label="Turnaround vzducholodě" value={`${String(data.airship.turnaroundMinutes)} min`} />
              <Row label="Verze záznamu" value={`v${String(data.version)}`} />
            </dl>

            {data.approval !== null && (
              <div
                className="mt-3 rounded-[var(--radius)] px-3 py-2 text-[13px]"
                style={{ background: 'var(--violet-dim)' }}
              >
                <p className="label-caps mb-1">Schvalovací proces</p>
                <p>
                  Žádost {dateTime(data.approval.requestedAt)} · lhůta{' '}
                  {dateTime(data.approval.deadlineAt)}
                </p>
                {data.approval.decidedAt === null ? (
                  <p className="text-[var(--text-secondary)]">
                    Čeká na rozhodnutí majitele flotily. Slot je do té doby držený.
                  </p>
                ) : (
                  <p className="text-[var(--text-secondary)]">
                    Rozhodnuto {dateTime(data.approval.decidedAt)}
                    {data.approval.decidedBy ? ` · ${data.approval.decidedBy.displayName}` : ''}
                    {data.approval.note ? ` · ${data.approval.note}` : ''}
                  </p>
                )}
              </div>
            )}

            {data.cancelledReason !== null && (
              <p className="mt-3 rounded-[var(--radius)] px-3 py-2 text-[13px]" style={{ background: 'var(--danger-dim)' }}>
                <span className="label-caps mr-2">Důvod zrušení</span>
                {data.cancelledReason}
              </p>
            )}
          </Card>

          {data.state === 'IN_FLIGHT' && (
            <Card>
              <CardHeader
                title="Živý let"
                subtitle={latest ? `poslední záznam ${relative(latest.recordedAt)}` : 'čekám na telemetrii'}
                action={<Pill tone="info">poll 10 s</Pill>}
              />
              {latest === undefined ? (
                <Skeleton className="h-24" />
              ) : (
                <>
                  <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-glass-strong)]">
                    <div
                      className="h-full rounded-full transition-[width] duration-700"
                      style={{ width: `${String(latest.progressPct)}%`, background: 'var(--info)' }}
                    />
                  </div>
                  <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    <Readout label="Postup" value={`${number(latest.progressPct, 1)} %`} />
                    <Readout label="Výška" value={`${number(latest.altitudeM)} m`} />
                    <Readout label="Ground speed" value={`${number(latest.groundSpeedKts)} kts`} />
                    <Readout label="Kurz" value={`${number(latest.headingDeg)}°`} />
                    <Readout label="Tlak helia" value={`${number(latest.heliumPressurePct, 1)} %`} />
                    <Readout label="Palivo" value={`${number(latest.fuelPct, 1)} %`} />
                  </dl>
                  <p className="mono mt-3 text-[11px] text-[var(--text-muted)]">
                    pozice {coords(latest.latitude, latest.longitude)} ·{' '}
                    {String(telemetry.data?.length ?? 0)} bodů trasy
                  </p>
                </>
              )}
            </Card>
          )}

          <Card>
            <CardHeader title="Notifikace k rezervaci" subtitle="Volání externí Notification Service" />
            {notifications.isLoading ? (
              <Skeleton className="h-16" />
            ) : (notifications.data?.items.length ?? 0) === 0 ? (
              <p className="text-[13px] text-[var(--text-muted)]">
                Notifikace odchází při potvrzení a při zrušení.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {notifications.data?.items.map((log) => (
                  <li
                    key={log.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius)] border border-[var(--border-subtle)] px-3 py-2 text-[13px]"
                  >
                    <span className="mono">{log.template}</span>
                    <span className="flex items-center gap-2">
                      <Pill
                        tone={log.status === 'SENT' ? 'accent' : log.status === 'FAILED' ? 'danger' : 'warn'}
                      >
                        {log.status}
                      </Pill>
                      <span className="text-[11px] text-[var(--text-muted)]">
                        {String(log.attempts)}× · {dateTime(log.createdAt)}
                      </span>
                    </span>
                    {log.lastError !== null && (
                      <span className="mono w-full text-[11px] text-[var(--danger)]">
                        {log.lastError}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader title="Cena" />
            <dl className="flex flex-col gap-1.5 text-[13px]">
              <Row
                inline
                label={`Let ${String(data.priceBreakdown.billableHours)} h × ${money(data.priceBreakdown.hourlyRateCzk)}`}
                value={moneyExact(data.priceBreakdown.flightCostCzk)}
              />
              <Row inline label="Pozemní obsluha" value={moneyExact(data.priceBreakdown.handlingFeeCzk)} />
              <Row inline label="Pasažéři" value={moneyExact(data.priceBreakdown.passengerFeeCzk)} />
            </dl>
            <div className="mt-3 flex items-baseline justify-between border-t border-[var(--border-subtle)] pt-3">
              <span className="label-caps">Celkem</span>
              <span className="mono text-lg font-bold text-[var(--accent)]">
                {moneyExact(data.priceCzk)}
              </span>
            </div>
            {canCancel && (
              <p className="mt-2 text-[12px] text-[var(--text-secondary)]">
                Při zrušení teď se vrací{' '}
                <span className="mono font-semibold">{moneyExact(data.refundIfCancelledNowCzk)}</span>.
              </p>
            )}
          </Card>

          {/* Boarding pass — the printable part. */}
          <Card className="print:break-inside-avoid">
            <CardHeader title="Boarding pass" subtitle="Vytiskni nebo ulož jako PDF" />
            <div className="rounded-[var(--radius)] border border-dashed border-[var(--border-strong)] p-4">
              <p className="label-caps">SWI Airship Rentals</p>
              <p className="mono mt-1 text-lg font-bold text-[var(--accent)]">
                {data.airship.registration}
              </p>
              <p className="mt-1 text-[13px]">{data.airship.name}</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
                <div>
                  <p className="label-caps">Odlet</p>
                  <p className="mono">{data.departureHub.code}</p>
                  <p className="text-[11px] text-[var(--text-secondary)]">{dateTime(data.startAt)}</p>
                </div>
                <div>
                  <p className="label-caps">Přílet</p>
                  <p className="mono">{data.arrivalHub.code}</p>
                  <p className="text-[11px] text-[var(--text-secondary)]">{dateTime(data.endAt)}</p>
                </div>
                <div>
                  <p className="label-caps">Pasažér</p>
                  <p>{data.renter.displayName}</p>
                </div>
                <div>
                  <p className="label-caps">Osob</p>
                  <p className="mono">{data.passengers}</p>
                </div>
              </div>
              <p className="mono mt-3 text-[9px] break-all text-[var(--text-muted)]">{data.id}</p>
              <div
                aria-hidden
                className="mt-2 h-8 w-full"
                style={{
                  backgroundImage: `repeating-linear-gradient(90deg, var(--text-primary) 0 ${String(1 + (data.id.charCodeAt(0) % 2))}px, transparent ${String(1 + (data.id.charCodeAt(0) % 2))}px 4px)`,
                }}
              />
            </div>
          </Card>

          <Card>
            <CardHeader title="Checklist před odletem" />
            <ul className="flex flex-col gap-2 text-[13px]">
              <Check
                ok={data.passengers <= data.airship.capacityPax}
                label={`Kapacita ${String(data.passengers)}/${String(data.airship.capacityPax)} pax`}
              />
              <Check
                ok={data.pilot !== null}
                label={data.pilot === null ? 'Pilot není přiřazen' : `Pilot ${data.pilot.displayName}`}
              />
              <Check
                ok={data.state === 'CONFIRMED' || data.state === 'IN_FLIGHT' || data.state === 'COMPLETED'}
                label="Rezervace je potvrzená"
              />
              <li className="text-[12px] text-[var(--text-muted)]">
                Max. vítr při startu {String(data.airship.maxWindSpeedKts)} kts · turnaround{' '}
                {String(data.airship.turnaroundMinutes)} min
              </li>
            </ul>
          </Card>

          <Card>
            <CardHeader title="Možné přechody" />
            {data.allowedTransitions.length === 0 ? (
              <p className="text-[13px] text-[var(--text-muted)]">
                Rezervace je v koncovém stavu, dál se nemění.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {data.allowedTransitions.map((target) => (
                  <Pill key={target} tone="neutral">
                    {data.state} → {target}
                  </Pill>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Zrušit rezervaci"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCancelOpen(false)}>
              Ponechat
            </Button>
            <Button
              variant="danger"
              loading={cancel.isPending}
              disabled={cancelReason.trim().length < 3}
              onClick={() => cancel.mutate()}
            >
              Zrušit rezervaci
            </Button>
          </>
        }
      >
        <p className="mb-3 text-[13px] text-[var(--text-secondary)]">
          Vratka při zrušení teď:{' '}
          <span className="mono font-semibold text-[var(--text-primary)]">
            {moneyExact(data.refundIfCancelledNowCzk)}
          </span>{' '}
          z {moneyExact(data.priceCzk)}. Nad 72 h před odletem se vrací vše, 24–72 h polovina, pod
          24 h nic.
        </p>
        <Field label="Důvod zrušení" hint="Alespoň 3 znaky, uloží se do auditu.">
          <Textarea
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            placeholder="Klient odvolal objednávku"
          />
        </Field>
        <p className="mt-3 text-[11px] text-[var(--text-muted)]">
          {RULE_LABELS.RULE_INVALID_TRANSITION} Let, který už vzlétl, zrušit nelze.
        </p>
      </Modal>

      <Modal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title="Zamítnout rezervaci"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>
              Zpět
            </Button>
            <Button
              variant="danger"
              loading={reject.isPending}
              disabled={rejectReason.trim().length < 3}
              onClick={() => reject.mutate()}
            >
              Zamítnout
            </Button>
          </>
        }
      >
        <p className="mb-3 text-[13px] text-[var(--text-secondary)]">
          Zamítnutí je konečné — rezervace přejde do stavu <strong>Zamítnuto</strong>, slot se
          uvolní a nájemce musí založit novou žádost. Není to storno, takže se na něj nevztahují
          storno pásma.
        </p>
        <Field label="Důvod zamítnutí" hint="Alespoň 3 znaky, uloží se do auditu a odejde nájemci.">
          <Textarea
            value={rejectReason}
            onChange={(event) => setRejectReason(event.target.value)}
            placeholder="Vzducholoď je vyhrazená pro firemní event"
          />
        </Field>
        <p className="mt-3 text-[11px] text-[var(--text-muted)]">
          {RULE_LABELS.RULE_APPROVAL_EXPIRED} Po uplynutí lhůty už rozhodnout nelze.
        </p>
      </Modal>
    </>
  );
}

function Row({ label, value, inline = false }: { label: string; value: string; inline?: boolean }) {
  if (inline) {
    return (
      <div className="flex items-baseline justify-between gap-3">
        <dt className="text-[var(--text-secondary)]">{label}</dt>
        <dd className="mono">{value}</dd>
      </div>
    );
  }
  return (
    <div className="flex items-baseline justify-between gap-3 rounded-[var(--radius)] border border-[var(--border-subtle)] px-3 py-2">
      <dt className="text-[12px] text-[var(--text-muted)]">{label}</dt>
      <dd className="text-right text-[13px] font-medium">{value}</dd>
    </div>
  );
}

function Readout({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[var(--radius)] border border-[var(--border-subtle)] px-2.5 py-2">
      <dt className="label-caps">{label}</dt>
      <dd className="mono mt-0.5 text-[13px] font-semibold">{value}</dd>
    </div>
  );
}

function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <span
        aria-hidden
        className="grid size-4 place-items-center rounded-full text-[10px]"
        style={{
          background: ok ? 'var(--accent-dim)' : 'var(--warn-dim)',
          color: ok ? 'var(--accent)' : 'var(--warn)',
        }}
      >
        {ok ? '✓' : '!'}
      </span>
      {label}
    </li>
  );
}
